import * as React from 'react'
import { EyeSlash } from '@phosphor-icons/react'
import { cn } from '../../lib/utils'
import { Stat } from '../core/stat'
import { ColorDot } from '../core/color-dot'
import { HelpPopover } from '../core/help-popover'
import {
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
  TooltipContent,
} from '../core/tooltip'
import {
  PeriodPicker,
  type PeriodValue,
  type PeriodQuickSelect,
} from '../core/period-picker'
import { accentBg, accentText } from '../core/badge-colors'
import { type BadgeColor } from '../core/badge'
import { WidgetMenu } from './widget-menu'

/**
 * Shared internals for the performance widgets — the content renderers
 * ({@link StatBoard}, {@link PipelineBar}, {@link TrendChart}, {@link LineChart}),
 * their data types, and the header period/hide controls. Consumed by the
 * standalone `StatsWidget` / `PipelineWidget` / `ActivityTrendWidget` /
 * `ValueTrendWidget` / `PipelineTrendWidget`, so the charts stay identical
 * however they're arranged.
 *
 * Charts are hand-built from tokens (no chart dependency), matching the fb-ui
 * zero-dep approach behind `EventCalendar`.
 */

/** One KPI tile in the stats board. */
export interface PerformanceStat {
  /** Stable key. */
  key: string
  /** Metric label — "Deals Opened". */
  label: React.ReactNode
  /** Metric value — `10`, `$12,400`, `-`. */
  value: React.ReactNode
  /** Icon shown in the stat's soft chip. */
  icon?: React.ReactNode
  /** Chip color (categorical; defaults to brand). */
  color?: BadgeColor
  /** Help text — rendered in a `HelpPopover` after the label. */
  help?: React.ReactNode
}

/**
 * One stage segment in the pipeline bar. `color` should be the stage's own
 * badge color so the widget matches the deal pipeline everywhere else — use the
 * shared stage palette (`STAGE_COLORS` / the app's `getStageColor`).
 */
export interface PipelineStageDatum {
  label: string
  /** Count (or amount) of deals in this stage — drives the segment width. */
  value: number
  color: BadgeColor
  /**
   * Raw CSS color for the segment + legend dot, overriding `color`. Use when the
   * app's own palette (e.g. user-assigned deal-stage colors) can't be expressed
   * as a `BadgeColor`; falls back to `color`'s token when unset.
   */
  colorValue?: string
  /**
   * Optional secondary detail (e.g. a formatted currency amount) appended after
   * the count in the segment's hover tooltip. The bar width and legend stay
   * count-based (`value`); this is extra context on hover only.
   */
  valueLabel?: React.ReactNode
}

/** A series (a bar within each month group) in the trend chart. */
export interface TrendSeries {
  key: string
  label: string
  color: BadgeColor
}

/** One month in the trend chart: a value per series (aligned to `series`). */
export interface TrendPoint {
  label: string
  /** Values aligned to the `series` array. */
  values: number[]
}

export interface PerformanceTrends {
  series: TrendSeries[]
  points: TrendPoint[]
  /** Format a value for the y-axis + bar tooltips (default `String`). */
  formatValue?: (n: number) => string
}

export type PerformanceTab = 'stats' | 'pipeline' | 'trends'

/** Relative "last N" quick-selects (custom ranges with a friendly label), from `now`. */
export function relativeQuickSelects(now: Date): PeriodQuickSelect[] {
  const back = (mutate: (d: Date) => void): Date => {
    const d = new Date(now)
    mutate(d)
    return d
  }
  return [
    { label: 'Last week', value: { mode: 'custom', start: back((d) => d.setDate(d.getDate() - 7)), end: now, label: 'Last week' } },
    { label: 'Last month', value: { mode: 'custom', start: back((d) => d.setMonth(d.getMonth() - 1)), end: now, label: 'Last month' } },
    { label: 'Last 3 months', value: { mode: 'custom', start: back((d) => d.setMonth(d.getMonth() - 3)), end: now, label: 'Last 3 months' } },
    { label: 'Last 6 months', value: { mode: 'custom', start: back((d) => d.setMonth(d.getMonth() - 6)), end: now, label: 'Last 6 months' } },
  ]
}

export interface PerformanceControlsProps {
  periodLabel?: React.ReactNode
  defaultPeriod?: PeriodValue
  onPeriodApply?: (value: PeriodValue) => void
  /** Override the period picker's quick-selects (defaults to the relative "last N" set). */
  quickSelects?: PeriodQuickSelect[]
  /** Hide the picker's fiscal mode toggle — a calendar + quick-selects only. */
  showModes?: boolean
  onHide?: () => void
  hideLabel?: string
}

/**
 * The shared header controls — a {@link PeriodPicker} with the relative
 * quick-selects, plus a "Hide widget" ⋯ menu when `onHide` is set. Drop into a
 * `CardAction`. Used by every performance widget so the period control behaves
 * the same whether the tabs are together or split.
 */
export const PerformanceControls = ({
  periodLabel = 'Period',
  defaultPeriod,
  onPeriodApply,
  quickSelects,
  showModes,
  onHide,
  hideLabel = 'Widget options',
}: PerformanceControlsProps) => {
  // A widget with neither a period to apply nor a hide action (e.g. a snapshot
  // card whose data isn't period-scoped) renders no header control at all —
  // rather than a picker that would silently do nothing.
  if (!onPeriodApply && !onHide) return null
  const resolvedQuickSelects = quickSelects ?? relativeQuickSelects(new Date())
  // Default to the last preset ("Last 6 months" in the relative set) when none supplied.
  const resolvedDefault =
    defaultPeriod ?? resolvedQuickSelects[resolvedQuickSelects.length - 1].value
  return (
    <div className="flex items-center gap-1.5">
      {onPeriodApply && (
        <PeriodPicker
          label={periodLabel}
          size="md"
          defaultValue={resolvedDefault}
          quickSelects={resolvedQuickSelects}
          neutralDefault={resolvedDefault}
          showModes={showModes}
          onApply={onPeriodApply}
        />
      )}
      {onHide && (
        <WidgetMenu
          label={hideLabel}
          items={[
            { key: 'hide', label: 'Hide widget', icon: <EyeSlash />, onSelect: onHide },
          ]}
        />
      )}
    </div>
  )
}
PerformanceControls.displayName = 'PerformanceControls'

/**
 * "Stats" — a grid of KPI tiles. `columns` fixes the grid to `1` (a vertical
 * stack, e.g. a narrow side column), `2` (a 2×2 for four stats), or `4` (one row);
 * the default is responsive (2 up on narrow, 4 up from `sm`).
 */
export const StatBoard = ({
  stats,
  columns,
}: {
  stats: PerformanceStat[]
  columns?: 1 | 2 | 4
}) => (
  <div
    className={cn(
      'grid gap-[0.75rem]',
      columns === 1
        ? 'grid-cols-1'
        : columns === 2
          ? 'grid-cols-2'
          : columns === 4
            ? 'grid-cols-4'
            : 'grid-cols-2 sm:grid-cols-4'
    )}
  >
    {stats.map((s) => (
      <Stat
        key={s.key}
        size="sm"
        color={s.color ?? 'success'}
        icon={s.icon}
        value={s.value}
        label={s.label}
        help={
          s.help ? (
            <HelpPopover triggerSize="sm" size="md" label={`About ${s.label}`}>
              {s.help}
            </HelpPopover>
          ) : undefined
        }
      />
    ))}
  </div>
)
StatBoard.displayName = 'StatBoard'

/** "Pipeline by stage" — one stacked proportion bar + a stage legend. */
export const PipelineBar = ({ pipeline }: { pipeline: PipelineStageDatum[] }) => {
  const total = pipeline.reduce((sum, s) => sum + s.value, 0)
  if (total === 0) {
    return (
      <p className="py-6 text-center text-sm text-fbui-gray-500">
        No open deals in this period.
      </p>
    )
  }
  return (
    <div className="flex flex-col gap-[1rem]">
      <TooltipProvider delayDuration={80}>
        <div
          className="flex h-3 w-full overflow-hidden rounded-full bg-fbui-gray-100"
          role="img"
          aria-label={pipeline.map((s) => `${s.label}: ${s.value}`).join(', ')}
        >
          {pipeline.map((s) => (
            <Tooltip key={s.label}>
              <TooltipTrigger asChild>
                <span
                  className={cn(
                    'h-full transition-opacity hover:opacity-80',
                    !s.colorValue && accentBg[s.color]
                  )}
                  style={{
                    width: `${(s.value / total) * 100}%`,
                    ...(s.colorValue ? { backgroundColor: s.colorValue } : {}),
                  }}
                />
              </TooltipTrigger>
              <TooltipContent size="sm">
                {s.label}: {s.value}
                {s.valueLabel != null && <> · {s.valueLabel}</>}
              </TooltipContent>
            </Tooltip>
          ))}
        </div>
      </TooltipProvider>
      <ul className="grid grid-cols-2 gap-x-12 gap-y-2">
        {pipeline.map((s) => (
          <li
            key={s.label}
            className="flex items-center gap-2 text-sm font-medium text-fbui-gray-600"
          >
            {s.colorValue ? (
              <span
                className="inline-block size-2 shrink-0 rounded-full"
                style={{ backgroundColor: s.colorValue }}
              />
            ) : (
              <ColorDot color={s.color} size="sm" />
            )}
            <span className="min-w-0 flex-1 truncate">{s.label}</span>
            <span className="font-semibold tabular-nums text-fbui-gray-900">
              {s.value}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}
PipelineBar.displayName = 'PipelineBar'

/** A "nice" axis maximum at or above `n` (1/2/5 × 10ᵏ), so ticks land on round numbers. */
const niceMax = (n: number): number => {
  if (n <= 0) return 1
  const pow = Math.pow(10, Math.floor(Math.log10(n)))
  const frac = n / pow
  const step = frac <= 1 ? 1 : frac <= 2 ? 2 : frac <= 5 ? 5 : 10
  return step * pow
}

/** The trend chart — a grouped vertical bar chart with gridlines + a legend. */
export const TrendChart = ({ trends }: { trends: PerformanceTrends }) => {
  const format = trends.formatValue ?? String
  const max = niceMax(Math.max(1, ...trends.points.flatMap((p) => p.values)))
  const ticks = [max, max / 2, 0]

  return (
    // Fills its container's height so the plot adapts to the card (with a floor).
    <div className="flex min-h-0 flex-1 flex-col gap-[1rem]">
      {/* Accessible data table (visually hidden): the SVG/bar plot is decorative
          (aria-hidden), so keyboard / screen-reader users read the values here. */}
      <table className="sr-only">
        <caption>Trend values by period</caption>
        <thead>
          <tr>
            <th scope="col">Series</th>
            {trends.points.map((p) => (
              <th key={p.label} scope="col">
                {p.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {trends.series.map((s, si) => (
            <tr key={s.key}>
              <th scope="row">{s.label}</th>
              {trends.points.map((p) => (
                <td key={p.label}>{format(p.values[si])}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {/* Plot: a grid — [y-axis | plot] over [_ | x-labels]. The y-axis and plot
          share the `1fr` row, so the ticks span exactly the plot height and line
          up with the gridlines; the labels sit under the plot column only. */}
      <TooltipProvider delayDuration={80}>
        <div
          aria-hidden="true"
          className="grid min-h-32 flex-1 grid-cols-[auto_1fr] grid-rows-[1fr_auto] gap-x-2"
        >
          {/* y-axis (row 1) */}
          <div className="flex flex-col justify-between text-right text-sm tabular-nums text-fbui-gray-400">
            {ticks.map((t) => (
              <span key={t}>{format(t)}</span>
            ))}
          </div>
          {/* plot area — gridlines + bars (row 1) */}
          <div className="relative">
            <div className="absolute inset-0 flex flex-col justify-between">
              {ticks.map((t) => (
                <span key={t} className="h-px w-full bg-fbui-gray-100" />
              ))}
            </div>
            <div className="relative flex h-full items-end justify-between gap-2">
              {trends.points.map((p) => (
                <div
                  key={p.label}
                  className="flex h-full flex-1 items-end justify-center gap-1"
                >
                  {p.values.map((v, i) => {
                    const series = trends.series[i]
                    return (
                      <Tooltip key={series?.key ?? i}>
                        <TooltipTrigger asChild>
                          <span
                            className={cn(
                              'w-2.5 rounded-t transition-all hover:opacity-80',
                              accentBg[series?.color ?? 'gray']
                            )}
                            style={{ height: v <= 0 ? '0%' : `${(v / max) * 100}%` }}
                          />
                        </TooltipTrigger>
                        <TooltipContent size="sm">
                          {p.label} · {series?.label}: {format(v)}
                        </TooltipContent>
                      </Tooltip>
                    )
                  })}
                </div>
              ))}
            </div>
          </div>
          {/* x-axis labels — row 2, under the plot column only */}
          <div className="col-start-2 flex justify-between gap-2 pt-2">
            {trends.points.map((p) => (
              <span
                key={p.label}
                className="flex-1 text-center text-sm text-fbui-gray-500"
              >
                {p.label}
              </span>
            ))}
          </div>
        </div>
      </TooltipProvider>

      {/* Legend — below the plot, the conventional position for a chart key.
          Hidden from AT since the data table above already names each series. */}
      <div
        aria-hidden="true"
        className="flex flex-wrap justify-center gap-x-5 gap-y-1.5"
      >
        {trends.series.map((s) => (
          <span
            key={s.key}
            className="flex items-center gap-2 text-sm font-medium text-fbui-gray-600"
          >
            <ColorDot color={s.color} size="sm" />
            {s.label}
          </span>
        ))}
      </div>
    </div>
  )
}
TrendChart.displayName = 'TrendChart'

/** One line in a {@link LineChart}. `axis` picks the left (default) or right scale. */
export interface LineSeries {
  key: string
  label: string
  color: BadgeColor
  /** One value per x-label (aligned to `LineChartData.labels`). */
  values: number[]
  /** Which y-scale this line uses — `left` (default) or a secondary `right` axis. */
  axis?: 'left' | 'right'
}

export interface LineChartData {
  /** X-axis labels (time buckets). */
  labels: string[]
  series: LineSeries[]
  /** Format the left-axis ticks + tooltips (default `String`). */
  leftFormat?: (n: number) => string
  /** Format the right-axis ticks + tooltips (default `String`). */
  rightFormat?: (n: number) => string
}

/**
 * LineChart — a hand-built (SVG, no chart dep) multi-series line chart with an
 * optional secondary right axis, for value / forecast trends over time. Each
 * series is a `<polyline>` (crisp `non-scaling-stroke`) with HTML dots overlaid
 * by percentage so they stay circular at any width. Left-axis series share one
 * scale; `axis: 'right'` series share a second (e.g. deal count beside value).
 * Fills its container height like {@link TrendChart}.
 */
export const LineChart = ({ data }: { data: LineChartData }) => {
  const leftFmt = data.leftFormat ?? String
  const rightFmt = data.rightFormat ?? String
  const leftSeries = data.series.filter((s) => (s.axis ?? 'left') === 'left')
  const rightSeries = data.series.filter((s) => s.axis === 'right')
  const leftMax = niceMax(Math.max(1, ...leftSeries.flatMap((s) => s.values)))
  const rightMax = rightSeries.length
    ? niceMax(Math.max(1, ...rightSeries.flatMap((s) => s.values)))
    : 1
  const n = data.labels.length
  // Center each point in its slot so points align with the flex x-labels below.
  const xPct = (i: number) => ((i + 0.5) / Math.max(1, n)) * 100
  const yPct = (v: number, max: number) => 100 - (max <= 0 ? 0 : (v / max) * 100)
  const leftTicks = [leftMax, leftMax / 2, 0]
  const rightTicks = [rightMax, rightMax / 2, 0]

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-[1rem]">
      {/* Accessible data table (visually hidden): the SVG line plot is decorative
          (aria-hidden), so keyboard / screen-reader users read the values here.
          Right-axis series carry their axis in the row header. */}
      <table className="sr-only">
        <caption>Series values over time</caption>
        <thead>
          <tr>
            <th scope="col">Series</th>
            {data.labels.map((l) => (
              <th key={l} scope="col">
                {l}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.series.map((s) => {
            const fmt = s.axis === 'right' ? rightFmt : leftFmt
            return (
              <tr key={s.key}>
                <th scope="row">
                  {s.label}
                  {s.axis === 'right' ? ' (right axis)' : ''}
                </th>
                {s.values.map((v, i) => (
                  <td key={data.labels[i] ?? i}>{fmt(v)}</td>
                ))}
              </tr>
            )
          })}
        </tbody>
      </table>
      {/* Plot: a grid — [left-axis | plot | right-axis?] over [_ | x-labels]. The
          axes share the plot's `1fr` row so their ticks line up with the
          gridlines; labels sit under the plot column only. */}
      <TooltipProvider delayDuration={80}>
        <div
          aria-hidden="true"
          className={cn(
            'grid min-h-32 flex-1 grid-rows-[1fr_auto] gap-x-2',
            rightSeries.length > 0
              ? 'grid-cols-[auto_1fr_auto]'
              : 'grid-cols-[auto_1fr]'
          )}
        >
          {/* Left axis (row 1) */}
          <div className="flex flex-col justify-between text-right text-sm tabular-nums text-fbui-gray-400">
            {leftTicks.map((t) => (
              <span key={t}>{leftFmt(t)}</span>
            ))}
          </div>

          {/* Plot area — gridlines + lines + dots (row 1) */}
          <div className="relative">
            <div className="absolute inset-0 flex flex-col justify-between">
              {leftTicks.map((t) => (
                <span key={t} className="h-px w-full bg-fbui-gray-100" />
              ))}
            </div>
            <svg
              className="absolute inset-0 h-full w-full overflow-visible"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              {data.series.map((s) => {
                const max = s.axis === 'right' ? rightMax : leftMax
                const pts = s.values
                  .map((v, i) => `${xPct(i)},${yPct(v, max)}`)
                  .join(' ')
                return (
                  <polyline
                    key={s.key}
                    points={pts}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    vectorEffect="non-scaling-stroke"
                    className={accentText[s.color]}
                  />
                )
              })}
            </svg>
            {data.series.map((s) => {
              const max = s.axis === 'right' ? rightMax : leftMax
              const fmt = s.axis === 'right' ? rightFmt : leftFmt
              return s.values.map((v, i) => (
                <Tooltip key={`${s.key}-${i}`}>
                  <TooltipTrigger asChild>
                    <span
                      className={cn(
                        'absolute size-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 ring-fbui-base-white transition-transform hover:scale-125',
                        accentBg[s.color]
                      )}
                      style={{ left: `${xPct(i)}%`, top: `${yPct(v, max)}%` }}
                    />
                  </TooltipTrigger>
                  <TooltipContent size="sm">
                    {data.labels[i]} · {s.label}: {fmt(v)}
                  </TooltipContent>
                </Tooltip>
              ))
            })}
          </div>

          {/* Right axis (row 1, only when a series uses it) */}
          {rightSeries.length > 0 && (
            <div className="flex flex-col justify-between text-left text-sm tabular-nums text-fbui-gray-400">
              {rightTicks.map((t) => (
                <span key={t}>{rightFmt(t)}</span>
              ))}
            </div>
          )}

          {/* X-axis labels — row 2, under the plot column only */}
          <div className="col-start-2 flex justify-between gap-2 pt-2">
            {data.labels.map((label) => (
              <span
                key={label}
                className="flex-1 text-center text-sm text-fbui-gray-500"
              >
                {label}
              </span>
            ))}
          </div>
        </div>
      </TooltipProvider>

      {/* Legend — below the plot, the conventional position for a chart key.
          Hidden from AT since the data table above already names each series. */}
      <div
        aria-hidden="true"
        className="flex flex-wrap justify-center gap-x-5 gap-y-1.5"
      >
        {data.series.map((s) => (
          <span
            key={s.key}
            className="flex items-center gap-2 text-sm font-medium text-fbui-gray-600"
          >
            <ColorDot color={s.color} size="sm" />
            {s.label}
          </span>
        ))}
      </div>
    </div>
  )
}
LineChart.displayName = 'LineChart'
